import { Package, Plus, Save, Search, ShieldOff, Trash2 } from 'lucide-react';
import Lightbox from 'yet-another-react-lightbox';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';

import { PageHeader } from '../components/ui/PageHeader';
import { ResponsiveStatsGrid } from '../components/ui/ResponsiveGrid';
import { ResponsiveModal, ModalFooter, ModalButton } from '../components/ui/ResponsiveModal';
import { Surface } from '../components/ui/Surface';
import { useAuth } from '../contexts/AuthContext';
import { useIsMobileOrTablet } from '../hooks/useMediaQuery';

// Modular Assets
import { useProductManagement } from './products/hooks/useProductManagement';
import { ProductTable } from './products/components/ProductTable';
import { ProductForm } from './products/components/ProductForm';

export function ProductManagement() {
  const { hasPermission } = useAuth();
  const isMobileOrTablet = useIsMobileOrTablet();

  const {
    searchTerm,
    setSearchTerm,
    isCreating,
    setIsCreating,
    editingProduct,
    warehouseFilter,
    setWarehouseFilter,
    page,
    setPage,
    lightboxOpen,
    setLightboxOpen,
    lightboxImages,
    lightboxIndex,
    deleteConfirmOpen,
    setDeleteConfirmOpen,
    productToDelete,
    setProductToDelete,
    dynamicTags,
    productsQuery,
    form,
    imageFields,
    imageInputRef,
    variantFields,
    handleRemoveVariant,
    deleteProductMutation,
    toggleProductStatusMutation,
    handleImageUpload,
    handleAddImageClick,
    handleEdit,
    handleCancelEdit,
    openLightbox,
    handleSubmit,
    removeImage,
    appendVariant,
  } = useProductManagement();

  if (!hasPermission('write')) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center space-y-4">
          <ShieldOff className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500" />
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Access Denied</h2>
          <p className="text-gray-600 dark:text-gray-400">Require Admin role to access</p>
        </div>
      </div>
    );
  }

  const statistics = productsQuery.data?.statistics;
  const pagination = productsQuery.data?.pagination;
  const products = productsQuery.data?.products || [];

  return (
    <div className="space-y-4 lg:space-y-6">
      <PageHeader
        title="จัดการสินค้า"
        description="เพิ่ม แก้ไข และจัดการสินค้าในระบบ"
        leading={<Package className="h-5 w-5 lg:h-6 lg:w-6" />}
        actions={(
          <button
            onClick={() => setIsCreating(!isCreating)}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-yellow-400 to-orange-500 border border-transparent px-3 lg:px-4 py-2 text-sm font-medium text-white shadow-sm hover:shadow-accent hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 touch-target"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">เพิ่มสินค้า</span>
            <span className="sm:hidden">เพิ่ม</span>
          </button>
        )}
      />

      <ResponsiveStatsGrid>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-stone-200 p-4 lg:p-6 hover:border-yellow-400 hover:shadow-accent transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-stone-600 dark:text-gray-400">สินค้าทั้งหมด</p>
              <p className="text-2xl lg:text-3xl font-mono font-bold text-stone-900 dark:text-white mt-1 lg:mt-2">
                {(pagination?.total || 0).toLocaleString('th-TH')}
              </p>
            </div>
            <div className="p-2 lg:p-3 bg-yellow-100 dark:bg-yellow-900/20 rounded-full">
              <Package className="h-6 w-6 lg:h-8 lg:w-8 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-stone-200 p-4 lg:p-6 hover:border-yellow-400 hover:shadow-accent transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-stone-600 dark:text-gray-400">สินค้าลดราคา</p>
              <p className="text-2xl lg:text-3xl font-mono font-bold text-orange-600 dark:text-white mt-1 lg:mt-2">
                {(statistics?.totalOnSale || 0).toLocaleString('th-TH')}
              </p>
            </div>
            <div className="p-2 lg:p-3 bg-red-100 dark:bg-red-900/20 rounded-full">
              <span className="text-xl lg:text-2xl">🔥</span>
            </div>
          </div>
        </div>
      </ResponsiveStatsGrid>

      <ResponsiveModal
        isOpen={isCreating}
        onClose={handleCancelEdit}
        title={editingProduct ? 'แก้ไขสินค้า' : 'เพิ่มสินค้าใหม่'}
        size="xl"
        mobileStyle="fullscreen"
        footer={(
          <ModalFooter>
            <ModalButton variant="secondary" onClick={handleCancelEdit}>ยกเลิก</ModalButton>
            <ModalButton variant="primary" onClick={handleSubmit} disabled={productsQuery.isFetching}>
              <Save className="w-4 h-4 mr-1.5" />
              {editingProduct ? 'บันทึกการแก้ไข' : 'สร้างสินค้า'}
            </ModalButton>
          </ModalFooter>
        )}
      >
        <ProductForm
          form={form}
          dynamicTags={dynamicTags}
          imageFields={imageFields}
          imageInputRef={imageInputRef}
          variantFields={variantFields}
          handleRemoveVariant={handleRemoveVariant}
          appendVariant={appendVariant}
          handleImageUpload={handleImageUpload}
          handleAddImageClick={handleAddImageClick}
          removeImage={removeImage}
          onOpenLightbox={(fields, index) => {
            const images = fields.map(f => f.preview);
            openLightbox(images as any, index);
          }}
          onSubmit={handleSubmit}
        />
      </ResponsiveModal>

      <Surface>
        <div className="flex flex-col sm:flex-row gap-4">
          <label className="relative flex-1 block">
            <span className="sr-only">ค้นหาสินค้า</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="ค้นหาสินค้า..."
              className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-11 pr-4 text-sm text-gray-700 shadow-inner focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-400 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
            />
          </label>
          <div className="flex p-1 bg-stone-100 dark:bg-gray-800 rounded-lg border border-stone-200 dark:border-gray-700">
            {[
              { id: undefined, label: 'ทั้งหมด' },
              { id: 'HYB', label: 'คลัง HYB' },
              { id: 'PP', label: 'คลัง PP' },
            ].map(wh => (
              <button
                key={wh.label}
                onClick={() => setWarehouseFilter(wh.id as any)}
                className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-all duration-200 ${warehouseFilter === wh.id
                  ? 'bg-white dark:bg-gray-700 text-yellow-600 dark:text-yellow-400 shadow-sm'
                  : 'text-stone-500 dark:text-gray-400 hover:text-stone-700 dark:hover:text-gray-200'
                  }`}
              >
                {wh.label}
              </button>
            ))}
          </div>
        </div>
      </Surface>

      <ProductTable
        products={products}
        isLoading={productsQuery.isLoading}
        isMobileOrTablet={isMobileOrTablet}
        searchTerm={searchTerm}
        onEdit={handleEdit}
        onDelete={(product) => {
          setProductToDelete(product);
          setDeleteConfirmOpen(true);
        }}
        onToggleStatus={(params) => toggleProductStatusMutation.mutate(params)}
        onOpenLightbox={openLightbox}
        pagination={pagination}
        page={page}
        onPageChange={setPage}
        onCreateClick={() => setIsCreating(true)}
        isTogglePending={toggleProductStatusMutation.isPending}
      />

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={lightboxImages.map((src) => ({ src }))}
        plugins={[Thumbnails, Zoom]}
        thumbnails={{ position: 'bottom', width: 120, height: 80, border: 1, borderRadius: 4, padding: 4, gap: 16 }}
        zoom={{ maxZoomPixelRatio: 3, scrollToZoom: true }}
        animation={{ fade: 250 }}
        controller={{ closeOnBackdropClick: true }}
      />

      <ResponsiveModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setProductToDelete(null);
        }}
        title="ยืนยันการลบสินค้า"
        size="sm"
        footer={(
          <ModalFooter>
            <ModalButton variant="secondary" onClick={() => { setDeleteConfirmOpen(false); setProductToDelete(null); }}>ยกเลิก</ModalButton>
            <ModalButton
              variant="primary"
              onClick={() => {
                if (productToDelete) {
                  deleteProductMutation.mutate(productToDelete.id);
                  setDeleteConfirmOpen(false);
                  setProductToDelete(null);
                }
              }}
              disabled={deleteProductMutation.isPending}
              className="!bg-red-600 hover:!bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              ลบสินค้า
            </ModalButton>
          </ModalFooter>
        )}
      >
        <div className="p-4 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
            <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">ต้องการลบสินค้านี้?</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{productToDelete?.name}</p>
          <p className="text-xs text-red-500">การดำเนินการนี้ไม่สามารถย้อนกลับได้</p>
        </div>
      </ResponsiveModal>
    </div>
  );
}

export default ProductManagement;
